import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Link2, Globe, AlertTriangle, Anchor, ExternalLink, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Users, ShieldAlert, RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  useBacklinksSummary,
  useReferringDomains,
  useBacklinksAnchors,
  useBacklinkChanges,
  useBacklinksTimeseries,
  useSpamScores,
  useBacklinkCompetitors,
  useBacklinksHistory,
  useDomainPages,
  useRefreshBacklinks,
} from '../hooks/useBacklinks'

interface BacklinksTabProps {
  siteId: string
  domain: string
  tier: string
}

function MetricCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${color ?? ''}`}>{value}</p>
      </CardContent>
    </Card>
  )
}

function AnchorBar({ anchor, backlinks, maxBacklinks }: { anchor: string; backlinks: number; maxBacklinks: number }) {
  const width = maxBacklinks > 0 ? (backlinks / maxBacklinks) * 100 : 0
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-sm min-w-0 flex-1 truncate" title={anchor}>{anchor || '(empty)'}</span>
      <div className="w-32 flex items-center gap-2 shrink-0">
        <div className="flex-1 bg-muted rounded-full h-2">
          <div
            className="bg-primary/70 h-2 rounded-full"
            style={{ width: `${Math.max(width, 2)}%` }}
          />
        </div>
        <span className="text-xs tabular-nums text-muted-foreground w-12 text-right">{backlinks.toLocaleString()}</span>
      </div>
    </div>
  )
}

function spamBg(score: number) {
  if (score <= 30) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  if (score <= 60) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
  return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
}

function BacklinkChangesSection({ siteId }: { siteId: string }) {
  const { data, isLoading } = useBacklinkChanges(siteId)
  const [showNew, setShowNew] = useState(true)
  const [showLost, setShowLost] = useState(true)

  if (isLoading) return null
  if (!data || !data.hasChanges) return null

  const newCount = data.newDomains?.length ?? 0
  const lostCount = data.lostDomains?.length ?? 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Referring Domain Changes</CardTitle>
        <p className="text-xs text-muted-foreground">
          Comparing latest snapshot with previous
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-4 text-sm">
          <span className="text-green-600 dark:text-green-400 font-medium">
            <TrendingUp className="inline h-3.5 w-3.5 mr-1" />
            +{newCount} new
          </span>
          <span className="text-red-600 dark:text-red-400 font-medium">
            <TrendingDown className="inline h-3.5 w-3.5 mr-1" />
            -{lostCount} lost
          </span>
        </div>

        {newCount > 0 && (
          <div>
            <button
              onClick={() => setShowNew(!showNew)}
              className="flex items-center gap-1 text-sm font-medium text-green-600 dark:text-green-400 hover:underline"
            >
              {showNew ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              New domains ({newCount})
            </button>
            {showNew && (
              <ul className="mt-1 space-y-0.5 pl-5 text-sm">
                {data.newDomains!.map((d) => (
                  <li key={d} className="text-green-700 dark:text-green-400">{d}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {lostCount > 0 && (
          <div>
            <button
              onClick={() => setShowLost(!showLost)}
              className="flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400 hover:underline"
            >
              {showLost ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Lost domains ({lostCount})
            </button>
            {showLost && (
              <ul className="mt-1 space-y-0.5 pl-5 text-sm">
                {data.lostDomains!.map((d) => (
                  <li key={d} className="text-red-700 dark:text-red-400 line-through">{d}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── Timeseries Chart with New/Lost Overlay ────────────────────

function TimeseriesChart({ siteId }: { siteId: string }) {
  const { data, isLoading } = useBacklinksTimeseries(siteId)
  const { data: historyData } = useBacklinksHistory(siteId)

  if (isLoading) return <Skeleton className="h-48 w-full" />

  const items = data?.items ?? []
  if (items.length < 2) return null

  const historyItems = historyData?.items ?? []

  // Build lookup by month for new/lost overlay
  const historyByMonth = new Map<string, { new_referring_domains: number; lost_referring_domains: number }>()
  for (const h of historyItems) {
    const month = new Date(h.date).toISOString().slice(0, 7) // YYYY-MM
    historyByMonth.set(month, { new_referring_domains: h.new_referring_domains, lost_referring_domains: h.lost_referring_domains })
  }

  const maxBacklinks = Math.max(...items.map((i) => i.backlinks), 1)
  const maxDomains = Math.max(...items.map((i) => i.referring_domains), 1)

  // Max new/lost for overlay scaling
  const allNewLost = historyItems.flatMap((h) => [h.new_referring_domains, h.lost_referring_domains])
  const maxNewLost = allNewLost.length > 0 ? Math.max(...allNewLost, 1) : 1

  const hasHistory = historyByMonth.size > 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Backlink Trends
        </CardTitle>
        <p className="text-xs text-muted-foreground">Monthly backlinks and referring domains over time</p>
      </CardHeader>
      <CardContent>
        {/* Legend */}
        <div className="flex gap-4 mb-3 text-xs flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-blue-500/70" />
            <span className="text-muted-foreground">Backlinks</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-green-500/70" />
            <span className="text-muted-foreground">Referring Domains</span>
          </div>
          {hasHistory && (
            <>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-emerald-400" />
                <span className="text-muted-foreground">New Domains</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-red-400" />
                <span className="text-muted-foreground">Lost Domains</span>
              </div>
            </>
          )}
        </div>

        {/* Main bar chart */}
        <div className="flex items-end gap-1 h-40">
          {items.map((item, i) => {
            const blHeight = (item.backlinks / maxBacklinks) * 100
            const rdHeight = (item.referring_domains / maxDomains) * 100
            const date = new Date(item.date)
            const label = date.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })
            const month = date.toISOString().slice(0, 7)
            const history = historyByMonth.get(month)

            let tooltip = `${label}\nBacklinks: ${item.backlinks.toLocaleString()}\nRef. Domains: ${item.referring_domains.toLocaleString()}\nRank: ${item.rank}`
            if (history) {
              tooltip += `\nNew Domains: +${history.new_referring_domains}\nLost Domains: -${history.lost_referring_domains}`
            }

            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-0.5 min-w-0 group" title={tooltip}>
                <div className="w-full flex items-end gap-px h-32">
                  <div
                    className="flex-1 bg-blue-500/70 rounded-t-sm transition-all group-hover:bg-blue-500"
                    style={{ height: `${Math.max(blHeight, 2)}%` }}
                  />
                  <div
                    className="flex-1 bg-green-500/70 rounded-t-sm transition-all group-hover:bg-green-500"
                    style={{ height: `${Math.max(rdHeight, 2)}%` }}
                  />
                </div>
                {(i === 0 || i === items.length - 1 || i % 3 === 0) && (
                  <span className="text-[10px] text-muted-foreground truncate w-full text-center">{label}</span>
                )}
              </div>
            )
          })}
        </div>

        {/* New/Lost overlay chart */}
        {hasHistory && historyItems.length > 1 && (
          <div className="mt-4 pt-3 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2">New / Lost Referring Domains</p>
            <div className="flex items-end gap-1 h-20">
              {historyItems.map((item, i) => {
                const newHeight = (item.new_referring_domains / maxNewLost) * 100
                const lostHeight = (item.lost_referring_domains / maxNewLost) * 100
                const date = new Date(item.date)
                const label = date.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5 min-w-0 group" title={`${label}\n+${item.new_referring_domains} new\n-${item.lost_referring_domains} lost`}>
                    <div className="w-full flex items-end gap-px h-16">
                      <div
                        className="flex-1 bg-emerald-400/70 rounded-t-sm transition-all group-hover:bg-emerald-400"
                        style={{ height: `${Math.max(newHeight, 2)}%` }}
                      />
                      <div
                        className="flex-1 bg-red-400/70 rounded-t-sm transition-all group-hover:bg-red-400"
                        style={{ height: `${Math.max(lostHeight, 2)}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="flex gap-6 mt-3 pt-3 border-t text-xs text-muted-foreground flex-wrap">
          <span>Latest: <strong className="text-foreground">{items[items.length - 1]?.backlinks.toLocaleString()}</strong> backlinks</span>
          <span><strong className="text-foreground">{items[items.length - 1]?.referring_domains.toLocaleString()}</strong> referring domains</span>
          <span>Rank: <strong className="text-foreground">{items[items.length - 1]?.rank}</strong></span>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Backlink Competitors Section ─────────────────────────────

function BacklinkCompetitorsSection({ siteId }: { siteId: string }) {
  const { data, isLoading } = useBacklinkCompetitors(siteId)

  if (isLoading) return <Skeleton className="h-32 w-full" />

  const competitors = data?.competitors ?? []
  if (!competitors.length) return null

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" />
          Backlink Competitors
          <span className="text-muted-foreground font-normal text-sm">({competitors.length})</span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">Domains sharing the most backlink sources with yours</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 font-medium">Domain</th>
                <th className="pb-2 font-medium text-right">Rank</th>
                <th className="pb-2 font-medium text-right">Shared Backlinks</th>
              </tr>
            </thead>
            <tbody>
              {competitors.map((c, i) => {
                const rankColor = c.rank >= 50 ? 'text-green-600 dark:text-green-400' :
                  c.rank >= 20 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-muted-foreground'
                return (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="py-2">
                      <a
                        href={`https://${c.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline flex items-center gap-1 text-primary"
                      >
                        {c.domain}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </td>
                    <td className={`py-2 text-right tabular-nums font-medium ${rankColor}`}>{c.rank}</td>
                    <td className="py-2 text-right tabular-nums">{c.intersections.toLocaleString()}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Best-Linked Pages Section ──────────────────────────────

function BestLinkedPagesSection({ siteId }: { siteId: string }) {
  const { data, isLoading } = useDomainPages(siteId)

  if (isLoading) return <Skeleton className="h-32 w-full" />

  const pages = data?.pages ?? []
  if (!pages.length) return null

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Your Best-Linked Pages
          <span className="text-muted-foreground font-normal text-sm">({pages.length})</span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">Pages on your domain with the most backlinks</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 font-medium">Page</th>
                <th className="pb-2 font-medium text-right">Backlinks</th>
                <th className="pb-2 font-medium text-right">Ref. Domains</th>
                <th className="pb-2 font-medium text-right">Dofollow</th>
                <th className="pb-2 font-medium text-right">Nofollow</th>
                <th className="pb-2 font-medium text-right">Rank</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p, i) => {
                let pathname = p.page
                try { pathname = new URL(p.page).pathname } catch { /* keep full URL */ }
                return (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="py-2 min-w-0">
                      <a
                        href={p.page}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline flex items-center gap-1 text-primary"
                      >
                        <span className="truncate max-w-[250px] inline-block" title={p.page}>
                          {pathname}
                        </span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                      {p.title && (
                        <p className="text-xs text-muted-foreground truncate max-w-[250px]" title={p.title}>
                          {p.title}
                        </p>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums font-medium">{p.backlinks.toLocaleString()}</td>
                    <td className="py-2 text-right tabular-nums">{p.referring_domains.toLocaleString()}</td>
                    <td className="py-2 text-right tabular-nums text-green-600 dark:text-green-400">{p.dofollow.toLocaleString()}</td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">{p.nofollow.toLocaleString()}</td>
                    <td className="py-2 text-right tabular-nums">{p.rank}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Main Component ───────────────────────────────────────────

export function BacklinksTab({ siteId, domain, tier }: BacklinksTabProps) {
  const { data: summaryData, isLoading: summaryLoading } = useBacklinksSummary(siteId)
  const { data: domainsData, isLoading: domainsLoading } = useReferringDomains(siteId)
  const { data: anchorsData, isLoading: anchorsLoading } = useBacklinksAnchors(siteId)
  const { data: spamData } = useSpamScores(siteId)
  const refreshBacklinks = useRefreshBacklinks(siteId)
  const isManaged = tier === 'managed'

  if (summaryLoading) return <Skeleton className="h-48 w-full" />

  if (!summaryData?.configured) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Link2 className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <h3 className="text-lg font-semibold">DataForSEO Not Configured</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Backlink analysis requires DataForSEO API credentials. Set the <code className="bg-muted px-1 py-0.5 rounded text-xs">DATAFORSEO_LOGIN</code> and <code className="bg-muted px-1 py-0.5 rounded text-xs">DATAFORSEO_PASSWORD</code> environment variables.
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            DataForSEO backlink requests cost ~$0.002 each. Cached 24 hours.
          </p>
        </CardContent>
      </Card>
    )
  }

  const summary = summaryData?.summary
  if (!summary) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-yellow-500 mb-3" />
          <h3 className="text-lg font-semibold">No Backlink Data Available</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            DataForSEO returned no data for <strong>{domain}</strong>. This can happen if:
          </p>
          <ul className="text-sm text-muted-foreground mt-2 text-left max-w-sm mx-auto space-y-1">
            <li>- The domain is new or not yet indexed by DataForSEO</li>
            <li>- The DataForSEO Backlinks subscription is not active</li>
            <li>- A previous empty result was cached (24hr TTL)</li>
          </ul>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => refreshBacklinks.mutate()}
            disabled={refreshBacklinks.isPending}
          >
            <RotateCw className={`h-3.5 w-3.5 mr-1.5 ${refreshBacklinks.isPending ? 'animate-spin' : ''}`} />
            {refreshBacklinks.isPending ? 'Checking...' : 'Check Again'}
          </Button>
          {refreshBacklinks.isSuccess && !refreshBacklinks.data?.summary && (
            <p className="text-xs text-muted-foreground mt-2">
              Still no data. The domain may not be indexed by DataForSEO yet.
            </p>
          )}
        </CardContent>
      </Card>
    )
  }

  const domains = domainsData?.domains ?? []
  const anchors = anchorsData?.anchors ?? []
  const maxAnchorBacklinks = anchors.reduce((max, a) => Math.max(max, a.backlinks), 1)

  // Build spam score lookup map
  const spamMap = new Map<string, number>()
  for (const s of spamData?.scores ?? []) {
    spamMap.set(s.target, s.spam_score)
  }
  const highSpamCount = Array.from(spamMap.values()).filter((s) => s > 60).length

  const rankColor = summary.domainRank >= 50 ? 'text-green-600 dark:text-green-400' :
    summary.domainRank >= 20 ? 'text-yellow-600 dark:text-yellow-400' :
    'text-red-600 dark:text-red-400'

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard label="Domain Rank" value={summary.domainRank} color={rankColor} />
        <MetricCard label="Total Backlinks" value={summary.totalBacklinks.toLocaleString()} />
        <MetricCard label="Referring Domains" value={summary.referringDomains.toLocaleString()} />
        <MetricCard label="Referring IPs" value={summary.referringIps.toLocaleString()} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard label="Nofollow Domains" value={summary.nofollowDomains.toLocaleString()} />
        <MetricCard label="Broken Backlinks" value={summary.brokenBacklinks.toLocaleString()} />
        <MetricCard label="Spam Score" value={summary.spamScore.toFixed(1)} />
        {spamMap.size > 0 && (
          <MetricCard
            label="High Spam Domains"
            value={`${highSpamCount} of ${spamMap.size}`}
            color={highSpamCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}
          />
        )}
      </div>

      {/* Timeseries chart (managed only) */}
      {isManaged && <TimeseriesChart siteId={siteId} />}

      {/* Backlink changes */}
      <BacklinkChangesSection siteId={siteId} />

      {/* Referring domains table with spam scores */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Top Referring Domains
            <span className="text-muted-foreground font-normal text-sm">({domains.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {domainsLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : domains.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No referring domain data.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Domain</th>
                    <th className="pb-2 font-medium text-right">Rank</th>
                    <th className="pb-2 font-medium text-right">Backlinks</th>
                    {spamMap.size > 0 && (
                      <th className="pb-2 font-medium text-center">
                        <span className="flex items-center justify-center gap-1">
                          <ShieldAlert className="h-3 w-3" />
                          Spam
                        </span>
                      </th>
                    )}
                    <th className="pb-2 font-medium text-right">First Seen</th>
                    <th className="pb-2 font-medium text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.slice(0, 50).map((d, i) => {
                    const spam = spamMap.get(d.domain)
                    return (
                      <tr key={i} className="border-b border-border/50 last:border-0">
                        <td className="py-2">
                          <a
                            href={`https://${d.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline flex items-center gap-1 text-primary"
                          >
                            {d.domain}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                        <td className="py-2 text-right tabular-nums">{d.rank}</td>
                        <td className="py-2 text-right tabular-nums">{d.backlinks.toLocaleString()}</td>
                        {spamMap.size > 0 && (
                          <td className="py-2 text-center">
                            {spam != null ? (
                              <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${spamBg(spam)}`}>
                                {spam}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                        )}
                        <td className="py-2 text-right text-xs text-muted-foreground">
                          {d.firstSeen ? new Date(d.firstSeen).toLocaleDateString('en-AU') : '-'}
                        </td>
                        <td className="py-2 text-center">
                          {d.isLost ? (
                            <span className="text-xs text-red-600 dark:text-red-400">Lost</span>
                          ) : (
                            <span className="text-xs text-green-600 dark:text-green-400">Live</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Anchor text distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Anchor className="h-4 w-4" />
            Anchor Text Distribution
            <span className="text-muted-foreground font-normal text-sm">({anchors.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {anchorsLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : anchors.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No anchor text data.</p>
          ) : (
            <div className="space-y-0">
              {anchors.slice(0, 30).map((a, i) => (
                <AnchorBar key={i} anchor={a.anchor} backlinks={a.backlinks} maxBacklinks={maxAnchorBacklinks} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Best-linked pages (managed only) */}
      {isManaged && <BestLinkedPagesSection siteId={siteId} />}

      {/* Backlink competitors (managed only) */}
      {isManaged && <BacklinkCompetitorsSection siteId={siteId} />}
    </div>
  )
}
